<script setup lang="ts">
import StudentRecipientSummaryContent from './StudentRecipientSummaryContent.vue';
import { useStudentRecipientLabel } from '@/composables/useStudentRecipientLabel';
import { useStudentSelectionLabel } from '@/composables/useStudentSelectionLabel';
import { TimelineTone, type TTimelineTone } from '@/core/timeline/timelineTone';
import { useClassStudents } from '@/composables/student/useClassStudents';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import type { IGenericUser } from '@/interfaces';
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    /** Student view: only this student is named, and the remaining `students` become "+n weitere". */
    focusedStudent?: IGenericUser | null;
    /** Label color for surfaces outside the timeline tones. */
    labelClass?: string;
    /** Every student the entry addresses, including the focused one. */
    students: IGenericUser[];
    tone?: TTimelineTone;
  }>(),
  {
    focusedStudent: null,
    labelClass: undefined,
    tone: TimelineTone.Default,
  },
);

const { currentClass } = useCurrentClass();
const { allStudents } = useClassStudents(() => currentClass.value?.id);
const { getStudentRecipientLabel } = useStudentRecipientLabel();
const recipientLabel = computed(() =>
  getStudentRecipientLabel({ focusedStudent: props.focusedStudent, students: props.students }),
);
const shownStudents = computed<IGenericUser[]>(() => (props.focusedStudent ? [props.focusedStudent] : props.students));
const { label, wholeClassSelected } = useStudentSelectionLabel(
  shownStudents,
  allStudents,
  () => recipientLabel.value.label,
);
</script>

<template>
  <StudentRecipientSummaryContent
    v-bind="props"
    :whole-class-selected="wholeClassSelected"
    :whole-class-label="label"
  />
</template>
